import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { MeasureBundleObject } from '@grow-therapy-team/seedling-components';

import { telehealthSoftBlockEnabledGate } from '../../statsig/gates';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { ProviderDrawerState, selectedBundleAtom } from '../state';
import { MeasureCompletedToast } from './MeasureCompletedToast';

const TOAST_KEY_PREFIX = 'measure-completed';
const FRESHNESS_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Returns true if the given timestamp falls within the freshness window
 * relative to the reference time (defaults to now).
 */
export function wasMeasureCompletedRecently(
  timestamp: string | undefined | null,
  referenceTime: number = Date.now(),
): boolean {
  if (!timestamp) return false;
  const time = new Date(timestamp).getTime();
  const elapsed = referenceTime - time;
  return elapsed >= 0 && elapsed <= FRESHNESS_WINDOW_MS;
}

/**
 * Checks whether measures were recently completed, either via the in-session
 * soft-block flow (measureCompletedAt on the presence map) or via any other
 * path by scanning bundle item responses.
 */
export function hasRecentMeasureCompletion(
  measureCompletedAt: string | undefined,
  measureBundles?: MeasureBundleObject[],
  admissionTime?: string,
): boolean {
  const referenceTime = admissionTime ? new Date(admissionTime).getTime() : Date.now();
  if (wasMeasureCompletedRecently(measureCompletedAt, referenceTime)) return true;
  if (!measureBundles?.length) return false;
  return measureBundles.some((bundle) =>
    bundle.measureBundleItems.some((item) =>
      wasMeasureCompletedRecently(item?.measureResponse?.completedAt, referenceTime),
    ),
  );
}

/**
 * Returns the most recent completion time for a bundle, considering both the
 * bundle-level `completedAt` and any completed item responses. Returns `null`
 * when the bundle has no completion timestamps at all.
 */
function getBundleCompletionTime(bundle: MeasureBundleObject): number | null {
  const timestamps = [
    bundle.completedAt,
    ...bundle.measureBundleItems.map((item) => item?.measureResponse?.completedAt),
  ]
    .filter((value): value is string => !!value)
    .map((value) => new Date(value).getTime());
  return timestamps.length ? Math.max(...timestamps) : null;
}

/**
 * Finds the bundle whose most recent completion is the newest, so we can
 * deep-link the provider straight into the check-in the client just finished.
 */
export function findMostRecentlyCompletedBundle(
  measureBundles?: MeasureBundleObject[],
): MeasureBundleObject | undefined {
  let mostRecent: { bundle: MeasureBundleObject; completedAt: number } | undefined;
  for (const bundle of measureBundles ?? []) {
    const completedAt = getBundleCompletionTime(bundle);
    if (completedAt !== null && (!mostRecent || completedAt > mostRecent.completedAt)) {
      mostRecent = { bundle, completedAt };
    }
  }
  return mostRecent?.bundle;
}

type PromptPayload = {
  visitorName: string;
  visitorUuid: string;
  patientShortId: string;
  selectedBundle: MeasureBundleObject | null;
};

/**
 * Shows a post-admission toast when a client has recently completed measures.
 * Returns a callback to be called in the fire-and-forget block after admission.
 */
export function useMeasureCompletedToastCallback() {
  const t = useTranslate();
  const { value: isSoftBlockEnabled } = useFeatureGate(telehealthSoftBlockEnabledGate);
  const room = useAtomValue(roomAtom);
  const openClientInformation = useOpenClientInformationCallback();
  const setSelectedBundle = useSetAtom(selectedBundleAtom);
  const [promptPayload, setPromptPayload] = useState<PromptPayload | null>(null);

  useEffect(
    function clearToastOnRoomDisconnect() {
      if (!room) {
        toast.closeByTag(TOAST_KEY_PREFIX);
      }
    },
    [room],
  );

  useEffect(
    function enqueueToast() {
      if (!promptPayload) return;

      const payload = promptPayload;
      setPromptPayload(null);

      // Deep-link straight into the check-in the client just finished. When we
      // can't identify a specific completed bundle (e.g. only the presence-map
      // flag was set), fall back to the full measures list.
      setSelectedBundle(payload.selectedBundle ?? undefined);
      openClientInformation(
        { visitorUuid: payload.visitorUuid, patientShortId: payload.patientShortId },
        ProviderDrawerState.MEASURE_RESPONSES,
      );

      toast.enqueue(
        (ariaProps) => (
          <MeasureCompletedToast
            {...ariaProps}
            heading={t('telehealth-core:preSessionMeasures.completedCheckInHeading', {
              clientName: payload.visitorName,
            })}
          />
        ),
        {
          key: `${TOAST_KEY_PREFIX}-${payload.patientShortId}`,
          tags: new Set([TOAST_KEY_PREFIX]),
          timeout: Infinity,
        },
      );
    },
    [promptPayload, openClientInformation, setSelectedBundle, t],
  );

  return useCallback(
    (
      {
        visitorName,
        visitorUuid,
        patientShortId,
      }: {
        visitorName: string;
        visitorUuid: string;
        patientShortId: string;
      },
      measureCompletedAt?: string,
      measureBundles?: MeasureBundleObject[],
      admissionTime?: string,
    ) => {
      if (!isSoftBlockEnabled) return;
      if (!hasRecentMeasureCompletion(measureCompletedAt, measureBundles, admissionTime)) {
        return;
      }

      setPromptPayload({
        visitorName,
        visitorUuid,
        patientShortId,
        selectedBundle: findMostRecentlyCompletedBundle(measureBundles) ?? null,
      });
    },
    [isSoftBlockEnabled],
  );
}
